import { useId } from 'react';
import { Burst } from './Burst.tsx';
import styles from './objects.module.css';

/** Where the centre of each of `n` pins is along the cylinder. */
export function pinX(i: number, n: number): number {
  return n <= 1 ? 150 : 34 + (i * 232) / (n - 1);
}

/** How far above the shear line a pin hangs when it is not set: different for each, always the same. */
export function restingLift(i: number): number {
  return 6 + ((i * 7) % 15);
}

/** Top of the driver pin when it sits on the shear line. */
const SET_TOP = 56;
const CEILING = 32;

/** The brass pin lock: a cylinder with its pins, springs and the shear line they must reach. */
export function LockObject({
  pins,
  set,
  fell,
}: {
  pins: number;
  set: ReadonlySet<number>;
  /** The pin that has just fallen back, if any. */
  fell?: number | null;
}) {
  const id = useId();
  const brass = `${id}-brass`;
  const steel = `${id}-steel`;
  const body = `${id}-body`;
  return (
    <div className={styles['hero']}>
      <svg className={styles['svg']} viewBox="0 0 300 150" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={brass} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--c-brass-light)" />
            <stop offset="0.45" stopColor="var(--c-brass)" />
            <stop offset="1" stopColor="var(--c-brass-dark)" />
          </linearGradient>
          <linearGradient id={steel} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#8a929c" />
            <stop offset="0.5" stopColor="#d9dee6" />
            <stop offset="1" stopColor="#7a8496" />
          </linearGradient>
          <clipPath id={body}>
            <rect x="10" y="14" width="280" height="118" rx="14" />
          </clipPath>
        </defs>
        <rect
          x="10"
          y="14"
          width="280"
          height="118"
          rx="14"
          fill={`url(#${brass})`}
          stroke="var(--c-brass-dark)"
          strokeWidth="2"
        />
        <rect x="16" y="18" width="268" height="7" rx="3.5" fill="#fff" opacity="0.22" />
        {Array.from({ length: pins }, (_, i) => {
          const x = pinX(i, pins);
          const isSet = set.has(i);
          // Set: the driver pin sits on the shear line. Not set: it hangs higher, on its spring.
          const rise = isSet ? 0 : -restingLift(i);
          return (
            <g key={i}>
              <rect x={x - 9} y="28" width="18" height="94" rx="4" fill="var(--c-ink-950)" />
              <polyline
                className={styles['pin']}
                fill="none"
                stroke="var(--c-brass-light)"
                strokeWidth="1.6"
                points={zigzag(x, CEILING, SET_TOP - 2)}
                style={{
                  transform: `scaleY(${(SET_TOP - 2 + rise - CEILING) / (SET_TOP - 2 - CEILING)})`,
                  transformOrigin: `${x}px ${CEILING}px`,
                }}
              />
              <g
                className={[styles['pin'], fell === i ? styles['pinFell'] : ''].join(' ')}
                style={{ transform: `translateY(${rise}px)` }}
              >
                <rect
                  x={x - 7}
                  y={SET_TOP}
                  width="14"
                  height="22"
                  rx="3"
                  fill={`url(#${steel})`}
                  stroke="#5a6476"
                />
                <rect
                  x={x - 6}
                  y={SET_TOP + 22}
                  width="12"
                  height="34"
                  rx="2"
                  fill={`url(#${brass})`}
                  stroke="var(--c-brass-dark)"
                />
              </g>
              <circle
                className={styles['led']}
                cx={x}
                cy="22"
                r="3.2"
                fill={isSet ? 'var(--c-neon-green)' : '#3a2f22'}
                stroke="var(--c-ink-950)"
                strokeWidth="1"
              />
              {isSet ? <Burst key={`set-${i}`} x={x} y={72} color="var(--c-brass-light)" /> : null}
            </g>
          );
        })}
        <line
          x1="18"
          x2="282"
          y1="72"
          y2="72"
          stroke="var(--c-neon-amber)"
          strokeWidth="1.2"
          strokeDasharray="5 4"
          opacity="0.75"
        />
        <g clipPath={`url(#${body})`}>
          <rect
            className={styles['shine']}
            x="0"
            y="0"
            width="46"
            height="150"
            fill="#fff"
            opacity="0.16"
          />
        </g>
        <path
          d="M132 132 h36 v10 q0 4 -4 4 h-28 q-4 0 -4 -4 z"
          fill="var(--c-brass-dark)"
          stroke="var(--c-brass)"
        />
      </svg>
    </div>
  );
}

/** A spring: a zigzag from `from` down to `to` at the pin's x. */
function zigzag(x: number, from: number, to: number): string {
  const n = 6;
  const pts: string[] = [];
  for (let i = 0; i <= n; i++) {
    const y = from + ((to - from) * i) / n;
    pts.push(`${(x + (i % 2 === 0 ? -5 : 5)).toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(' ');
}
